import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col } from "@/Components/ui";;
import toast from "react-hot-toast";
import { API, BACKEND_HOST } from "../../../api";
import type { RecommendedReview, RecommendedReviewPlatform } from "./recommendedReview.types";

interface ReviewModalProps {
    isOpen: boolean;
    toggle: () => void;
    review: RecommendedReview | null;
    onSave: (review: RecommendedReview) => void;
    siteId: string | number;
}

const PLATFORM_OPTIONS: { value: RecommendedReviewPlatform; label: string }[] = [
    { value: "naver_blog", label: "네이버 블로그" },
    { value: "instagram", label: "인스타그램" },
    { value: "youtube", label: "유튜브" },
    { value: "web", label: "웹/블로그" },
    { value: "other", label: "기타" },
];

const ReviewModal: React.FC<ReviewModalProps> = ({
    isOpen,
    toggle,
    review,
    onSave,
    siteId,
}) => {
    const [formData, setFormData] = useState<RecommendedReview>({
        platform: "web",
        source_url: "",
        title: "",
        content: "",
        author_name: "",
        author_profile_image: "",
        thumbnail_url: "",
        rating: 5,
        is_active: true,
    });

    const [isSaving, setIsSaving] = useState(false);
    const [thumbnailBase64, setThumbnailBase64] = useState<string | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

    useEffect(() => {
        if (review) {
            setFormData({ ...review });
            setThumbnailBase64(null);
            setThumbnailPreview(review.thumbnail_url || null);
        } else {
            setFormData({
                platform: "web",
                source_url: "",
                title: "",
                content: "",
                author_name: "",
                author_profile_image: "",
                thumbnail_url: "",
                rating: 5,
                is_active: true,
            });
            setThumbnailBase64(null);
            setThumbnailPreview(null);
        }
    }, [review, isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (name === "thumbnail_url") {
            setThumbnailBase64(null);
            setThumbnailPreview(value ? String(value) : null);
        }
    };

    const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        if (!file) {
            setThumbnailBase64(null);
            setThumbnailPreview(formData.thumbnail_url || null);
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            setThumbnailBase64(result || null);
            setThumbnailPreview(result || null);
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!formData.source_url && !formData.title) {
            toast.error("출처 URL 또는 제목을 입력해주세요.");
            return;
        }

        setIsSaving(true);

        try {
            const payload: Record<string, any> = {
                ...formData,
                site: siteId,
                rating: formData.rating ? parseFloat(formData.rating.toString()) : null,
            };

            if (thumbnailBase64) {
                payload.thumbnail_base64 = thumbnailBase64;
            }

            let response;
            if (review?.id) {
                response = await API.put(`${BACKEND_HOST}/sites/recommended-reviews/${review.id}/`, payload);
            } else {
                response = await API.post(`${BACKEND_HOST}/sites/recommended-reviews/`, payload);
            }

            onSave(response.data);
            toast.success("리뷰 정보가 저장되었습니다.");
            toggle();
        } catch (error: any) {
            console.error("Save review error:", error);
            let errorMessage = "리뷰 저장 중 오류가 발생했습니다.";
            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data === 'object') {
                    const messages = Object.keys(data).map(key => {
                        const val = data[key];
                        return `${key}: ${Array.isArray(val) ? val.join(', ') : val}`;
                    });
                    if (messages.length > 0) errorMessage = messages.join('\n');
                } else if (typeof data === 'string' && data.length < 100) {
                    errorMessage = data;
                }
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={toggle} size="xl"
            title={review ? "추천 리뷰 수정" : "새 추천 리뷰 등록"}
        >
            <ModalBody>
                <Form>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="title">제목</Label>
                                <Input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="리뷰 제목을 입력하세요."
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="source_url">출처 URL</Label>
                                <Input
                                    type="text"
                                    id="source_url"
                                    name="source_url"
                                    value={formData.source_url}
                                    onChange={handleInputChange}
                                    placeholder="리뷰 원문 URL을 입력하세요."
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="platform">플랫폼</Label>
                                <Input
                                    type="select"
                                    id="platform"
                                    name="platform"
                                    value={formData.platform}
                                    onChange={handleInputChange}
                                >
                                    {PLATFORM_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="rating">평점</Label>
                                <Input
                                    type="number"
                                    id="rating"
                                    name="rating"
                                    min="0"
                                    max="5"
                                    step="0.1"
                                    value={formData.rating}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="content">내용</Label>
                                <Input
                                    type="textarea"
                                    id="content"
                                    name="content"
                                    {...({ rows: 5 } as any)}
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    placeholder="리뷰 내용을 입력하세요."
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="author_name">작성자</Label>
                                <Input
                                    type="text"
                                    id="author_name"
                                    name="author_name"
                                    value={formData.author_name}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="thumbnail_url">이미지 URL</Label>
                                <Input
                                    type="text"
                                    id="thumbnail_url"
                                    name="thumbnail_url"
                                    value={formData.thumbnail_url}
                                    onChange={handleInputChange}
                                    placeholder="대표 이미지 URL을 입력하세요."
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="thumbnail_file">이미지 업로드</Label>
                                <Input
                                    type="file"
                                    id="thumbnail_file"
                                    accept="image/*"
                                    onChange={handleThumbnailFileChange}
                                />
                                <p className="text-[10px] text-gray-400 mt-2">
                                    * 업로드한 이미지는 URL보다 우선 적용됩니다.
                                </p>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>미리보기</Label>
                                <div className="w-full h-[140px] bg-gray-50 rounded-xl overflow-hidden border border-gray-200 flex items-center justify-center">
                                    {thumbnailPreview || formData.thumbnail_url ? (
                                        <img
                                            src={(thumbnailPreview || formData.thumbnail_url) as string}
                                            alt="Review thumbnail preview"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-[10px] text-gray-400">NO IMAGE</span>
                                    )}
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label htmlFor="author_profile_image">프로필 이미지 URL</Label>
                                <Input
                                    type="text"
                                    id="author_profile_image"
                                    name="author_profile_image"
                                    value={formData.author_profile_image}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label>
                            <Input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleInputChange}
                            />{' '}
                            노출 여부
                        </Label>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={toggle} disabled={isSaving}>취소</Button>
                <Button color="success" onClick={handleSave} disabled={isSaving}>{isSaving ? "저장 중..." : "저장하기"}</Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReviewModal;
